'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { ProcessedMinimap, ProcessedLocation, MarkerType } from '@/types/minimap';
import { useIdPath } from '@/contexts/IdPathProvider';
import { useLanguage } from '@/contexts/I18nProvider';
import { MapStack } from '@/templates/Heritage/utils/MapStack';
import minimapServices from '@/templates/Heritage/utils/services/minimap';
import groupViewServices from '@/templates/Heritage/utils/services/groupView';
import {
    resolveMarkerPresentation,
    generateNumberedBadgeSvgDataUrl,
    resolveNormalIconUrl,
    isLocationMatchingScene,
    hasLocationSceneTarget,
    parseLocationConfig,
    DEFAULT_HOTSPOT_ACTIVE_SCALE,
    DEFAULT_NUMBERED_PALETTE,
} from '@/utils/minimapMarkerDisplay';

// Types
interface EnhancedProcessedLocation extends ProcessedLocation {
    marker_type?: string;
    title?: string;
    marker_name?: string;
    icon?: string;
    scale?: number;
    ref?: string;
    code?: string;
    config?: Record<string, unknown> | string | null;
    translations?: Array<{ language_id: string; title?: string; url_map?: string | null }>;
    _highlightCurrentByGroup?: boolean;
}

const dedupeImageUrls = (values: string[]) => {
    const seen = new Set<string>();
    const next: string[] = [];

    values.forEach((value) => {
        const normalized = String(value || '').trim();
        if (!normalized) return;
        if (seen.has(normalized)) return;
        seen.add(normalized);
        next.push(normalized);
    });

    return next;
};

const isLikelyImageUrl = (value: unknown) => {
    const normalized = String(value || '').trim();
    if (!normalized) return false;

    if (
        normalized.startsWith('http://')
        || normalized.startsWith('https://')
        || normalized.startsWith('//')
        || normalized.startsWith('/upload/')
        || normalized.startsWith('upload/')
        || normalized.startsWith('data:image/')
    ) {
        return true;
    }

    return /\.(png|jpe?g|webp|gif|svg|bmp|avif)(\?|#|$)/i.test(normalized);
};

const extractImageUrlsDeep = (value: unknown): string[] => {
    if (value === null || value === undefined) return [];

    if (Array.isArray(value)) {
        return value.flatMap((item) => extractImageUrlsDeep(item));
    }

    if (typeof value === 'object') {
        return Object.values(value as Record<string, unknown>)
            .flatMap((item) => extractImageUrlsDeep(item));
    }

    if (typeof value !== 'string') return [];

    const trimmed = value.trim();
    if (!trimmed) return [];

    if (
        (trimmed.startsWith('{') && trimmed.endsWith('}'))
        || (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
        try {
            return extractImageUrlsDeep(JSON.parse(trimmed));
        } catch {
            // Fall through to plain-text splitting below.
        }
    }

    return trimmed
        .split(/\r?\n|,|;/)
        .map((item) => item.trim())
        .filter((item) => isLikelyImageUrl(item));
};

const parseImage2DUrlsFromConfig = (rawConfig: unknown, fallbackUrl?: string | number | null) => {
    const fallback = String(fallbackUrl || '').trim();
    const safeFallback = isLikelyImageUrl(fallback) ? fallback : '';
    const imageUrls = extractImageUrlsDeep(rawConfig);

    return dedupeImageUrls([
        ...imageUrls,
        safeFallback,
    ]);
};

const parseMarkerConfigObject = (rawConfig: unknown): Record<string, unknown> => {
    if (!rawConfig) return {};

    if (typeof rawConfig === 'string') {
        try {
            const parsed = JSON.parse(rawConfig);
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
                ? parsed as Record<string, unknown>
                : {};
        } catch {
            return {};
        }
    }

    if (typeof rawConfig === 'object' && !Array.isArray(rawConfig)) {
        return rawConfig as Record<string, unknown>;
    }

    return {};
};

export interface KrpanoInterface {
    call: (cmd: string) => unknown;
    set: (name: string, value: unknown) => void;
    get: (name: string) => unknown;
}

interface EmbedPanoConfig {
    xml: string;
    id: string;
    target: string;
    html5?: string;
    mobilescale?: number;
    passQueryParameters?: boolean;
    security?: string;
    sameorigin?: boolean;
    cross?: boolean;
    onready?: (krpano_interface: KrpanoInterface) => void;
    onerror?: (error: string) => void;
    wmode?: string;
    bgcolor?: string;
}

declare global {
    interface Window {
        embedpano: (config: EmbedPanoConfig) => void;
        changeMap: (mapId: number) => void;
        changeMapHandlers: Record<string, (mapId: number) => void>;
        // changeViewByMarker: (sceneId: string, type?: string) => void;
        changeViewByMarker: (sceneId: string, type?: string) => void;
        changeViewByMarkerHandlers: Record<string, (sceneId: string, type?: string, locationId?: string | number) => void>;
        removepano?: (id: string) => void;
        onclickMarkerInAdmin?: (locationId: number) => void;
    }
}

export interface UseKrpanoMapOptions {
    mapId?: string;
    targetElementId?: string;
    xmlUrl?: string;
    onMarkerClick?: () => void;
    onMapReady?: (krpano: KrpanoInterface) => void;
    onError?: (error: string) => void;
    onLocationsLoaded?: (locations: EnhancedProcessedLocation[]) => void;
    onImage2DMarkerClick?: (payload: {
        id: string;
        title: string;
        urls: string[];
        location?: EnhancedProcessedLocation;
    }) => void;
    currentScene?: string;
    customSceneClickHandler?: (sceneId: string, locationId?: string | number) => void;
    customGroupClickHandler?: (mapId: string) => void;
    isMapForAdmin?: boolean;
    onclickMarkerInAdmin?: (locationId: number) => void;
    enableZoomPan?: boolean;
    accessToken?: string;
    hoveredLocationId?: string | number | null;
    onMarkerHover?: (locationId: string | number | null) => void;
    selectedLocationId?: string | number | null;
    onMarkerSelect?: (locationId: string | number | null) => void;
    mapMarkerDisplay?: unknown;
}

export interface MapTransform {
    scale: number;
    x: number;
    y: number;
    /** Natural width of the map image (layer[map].imagewidth) in KRPano CSS pixels */
    imageWidth: number;
    /** Natural height of the map image (layer[map].imageheight) in KRPano CSS pixels */
    imageHeight: number;
}

export interface UseKrpanoMapReturn {
    // State
    isLoading: boolean;
    isReady: boolean;
    error: string | null;
    noMapData: boolean;
    minimapData: ProcessedMinimap | null;
    locations: EnhancedProcessedLocation[];
    markerTypes: MarkerType[];
    mapTransform: MapTransform;

    // Refs
    krpanoRef: React.RefObject<KrpanoInterface | null>;
    targetElementRef: React.RefObject<HTMLDivElement>;

    // Actions
    initialize: () => Promise<void>;
    changeMap: (mapId: number, forceChange?: boolean) => Promise<void>;
    changeViewByMarker: (sceneId: string, type?: string) => void;
    goBack: () => Promise<void>;
    retry: () => void;
    zoomIn: () => void;
    zoomOut: () => void;
    resetZoom: () => void;
    /** Preload a map image early so decode does not spike memory at popup-open time on iPhone */
    preloadMapImage: (rawUrl: string) => void;

    // History
    mapHistory: MapStack;
    canGoBack: boolean;

    // Hover Tooltip
    hoverTooltip: { text: string; visible: boolean };
    hideTooltip: () => void;
}

const DEFAULT_XML_URL = 'https://sanpham.starglobal3d.com/managements/user_FE/theme/heritage/minimap/minimap-platform.xml';
const DEFAULT_TARGET_ID = 'krpano_map_pano';

// Zoom limits
const MIN_ZOOM = 0.4;
const MAX_ZOOM_DEFAULT = 10.0;
const MAX_ZOOM_IPHONE = 4.0;
const MAX_ZOOM_IOS_SAFARI = 6.0;
const MIN_INITIAL_SCALE = 0.02;
const MAX_INITIAL_SCALE = 1.0;

export function animateHotspot(
    krpano: any,
    name: string,
    elementType: 'layer' | 'hotspot' = 'hotspot',
    onStop?: () => void
) {
    let frame = 0;
    const lastFrame = 24;
    const frameHeight = 200;
    const frameWidth = 200;

    const stop = () => {
        clearInterval(interval);
        try {
            onStop?.();
        } catch {}
    };

    const interval = setInterval(() => {
        if (!krpano || typeof krpano.get !== 'function') {
            stop();
            return;
        }

        const isAlive = Boolean(
            krpano.get(`${elementType}[${name}].name`) ??
            krpano.get(`${elementType}[${name}].url`) ??
            krpano.get(`${elementType}[${name}].visible`)
        );

        if (!isAlive) {
            stop();
            return;
        }

        frame = (frame + 1) % (lastFrame + 1);
        const y = frame * frameHeight;

        try {
            krpano.set(`${elementType}[${name}].crop`, `0|${y}|${frameWidth}|${frameHeight}`);
        } catch {
            stop();
        }
    }, 40);

    return interval; // để stop khi cần
}

export function useKrpanoMap(options: UseKrpanoMapOptions = {}): UseKrpanoMapReturn {
    const { mapId, targetElementId = DEFAULT_TARGET_ID, xmlUrl = DEFAULT_XML_URL, onMarkerClick, onMapReady, onError, onLocationsLoaded, onImage2DMarkerClick, customSceneClickHandler, customGroupClickHandler, isMapForAdmin, onclickMarkerInAdmin, enableZoomPan = true, accessToken } = options;

    // Compute a unique KRPano pano ID per targetElementId so that multiple
    // KrpanoMapViewer instances on the same page don't conflict with each other.
    const krpanoPanoId = `krpano_${targetElementId || DEFAULT_TARGET_ID}`;

    const { idPath } = useIdPath();
    const { locale } = useLanguage();

    // Define global functions for KRPano
    useEffect(() => {
        if (onclickMarkerInAdmin) {
            window.onclickMarkerInAdmin = onclickMarkerInAdmin;
        }
    }, [onclickMarkerInAdmin]);

    // State
    const [isLoading, setIsLoading] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [noMapData, setNoMapData] = useState(false);
    const [minimapData, setMinimapData] = useState<ProcessedMinimap | null>(null);
    const minimapDataRef = useRef<ProcessedMinimap | null>(null);
    const [locations, setLocations] = useState<EnhancedProcessedLocation[]>([]);
    const [markerTypes, setMarkerTypes] = useState<MarkerType[]>([]);
    const [mapTransform, setMapTransform] = useState<MapTransform>({ scale: 1, x: 0, y: 0, imageWidth: 0, imageHeight: 0 });

    // Hover tooltip & two-way marker highlight sync
    const markerTitlesRef = useRef<Record<string, string>>({});
    const markerClickHandlersRef = useRef<Record<string, () => void>>({});
    const markerBaseScalesRef = useRef<Record<string, number>>({});
    const markerBaseZordersRef = useRef<Record<string, number>>({});
    const currentlyHoveredMarkerRef = useRef<string | null>(null);
    const onMarkerHoverRef = useRef(options.onMarkerHover);
    onMarkerHoverRef.current = options.onMarkerHover;
    const onMarkerSelectRef = useRef(options.onMarkerSelect);
    onMarkerSelectRef.current = options.onMarkerSelect;
    const currentSceneRef = useRef(options.currentScene);
    currentSceneRef.current = options.currentScene;
    const selectedLocationIdRef = useRef(options.selectedLocationId);
    selectedLocationIdRef.current = options.selectedLocationId;
    const onMapReadyRef = useRef(onMapReady);
    onMapReadyRef.current = onMapReady;
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;
    const onLocationsLoadedRef = useRef(onLocationsLoaded);
    onLocationsLoadedRef.current = onLocationsLoaded;
    const sceneGroupIdsCacheRef = useRef<Map<string, string[]>>(new Map());
    const sceneGroupIdsPendingRef = useRef<Map<string, Promise<string[]>>>(new Map());
    const image2dMarkerDataRef = useRef<Record<string, {
        title: string;
        urls: string[];
        location?: EnhancedProcessedLocation;
    }>>({});
    const [hoverTooltip, setHoverTooltip] = useState<{ text: string; visible: boolean }>({ text: '', visible: false });

    // Refs
    const krpanoRef = useRef<KrpanoInterface | null>(null);
    const targetElementRef = useRef<HTMLDivElement>(null);
    const initializationRef = useRef(false);
    const lastInitTime = useRef(0);
    const baseScaleRef = useRef<number>(0);
    const mapFitGenerationRef = useRef(0);
    const syncActiveRef = useRef(false);
    const hotspotIntervalsRef = useRef<ReturnType<typeof setInterval>[]>([]);
    const activeMarkerIntervalsRef = useRef<Record<string, ReturnType<typeof setInterval>>>({});
    const [mapHistory] = useState(new MapStack());
    const currentMarkerDisplayRef = useRef<unknown>(options.mapMarkerDisplay ?? null);

    useEffect(() => {
        if (options.mapMarkerDisplay !== undefined && options.mapMarkerDisplay !== null) {
            currentMarkerDisplayRef.current = options.mapMarkerDisplay;
        }
    }, [options.mapMarkerDisplay]);

    const clearHotspotIntervals = useCallback(() => {
        Object.values(activeMarkerIntervalsRef.current).forEach((intervalId) => clearInterval(intervalId));
        activeMarkerIntervalsRef.current = {};
        hotspotIntervalsRef.current.forEach((intervalId) => clearInterval(intervalId));
        hotspotIntervalsRef.current = [];
    }, []);

    const forceLoseWebGLContext = useCallback(() => {
        try {
            const panoContainer = document.getElementById(krpanoPanoId);
            const canvas = (panoContainer?.querySelector('canvas') as HTMLCanvasElement | null)
                || (document.querySelector(`#${krpanoPanoId} canvas`) as HTMLCanvasElement | null);

            if (!canvas) return;

            const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
            const loseContext = gl?.getExtension('WEBGL_lose_context');
            loseContext?.loseContext();
        } catch (error) {
            console.warn('⚠️ Failed to force WebGL context release:', error);
        }
    }, [krpanoPanoId]);

    // Helpers
    const getCurrentLanguage = useCallback(() => locale || 'vn', [locale]);

    const resolveMarkerTitleForLocale = useCallback((item?: EnhancedProcessedLocation | null): string => {
        if (!item) return '';
        const locLower = (locale || 'vn').toLowerCase();
        const matched = item.translations?.find((t: any) => {
            const tId = String(t.language_id || '').toLowerCase();
            return tId === locLower ||
                (locLower === 'en' && tId === 'eng') ||
                (locLower === 'eng' && tId === 'en') ||
                (locLower === 'vn' && (tId === 'vi' || tId === 'vie')) ||
                (locLower === 'vi' && (tId === 'vn' || tId === 'vie'));
        });
        return matched?.title || item.translations?.[0]?.title || item.title || item.marker_name || '';
    }, [locale]);

    const getSceneGroupIdsBySceneName = useCallback(async (sceneIdentifier: string): Promise<string[]> => {
        const normalizedScene = String(sceneIdentifier || '').trim();
        if (!normalizedScene || !idPath) {
            return [];
        }

        const currentLang = getCurrentLanguage();
        const cacheKey = `${idPath}:${currentLang}:${normalizedScene}`;
        const cached = sceneGroupIdsCacheRef.current.get(cacheKey);
        if (cached) {
            return cached;
        }

        const pending = sceneGroupIdsPendingRef.current.get(cacheKey);
        if (pending) {
            return pending;
        }

        const queryPromise = (async () => {
            try {
                const response = await groupViewServices.getGroupOfCurrentScene({
                    id_path: idPath,
                    language: currentLang,
                    scene_id: normalizedScene,
                });

                const groups = (response && typeof response === 'object' && Array.isArray((response as { data?: unknown }).data))
                    ? ((response as { data?: unknown[] }).data || [])
                    : [];
                const groupIds = groups
                    .map((group) => {
                        if (!group || typeof group !== 'object') return '';
                        const groupRecord = group as Record<string, unknown>;
                        return String(groupRecord.id ?? groupRecord.group_id ?? '').trim();
                    })
                    .filter(Boolean);

                sceneGroupIdsCacheRef.current.set(cacheKey, groupIds);
                return groupIds;
            } catch {
                return [];
            } finally {
                sceneGroupIdsPendingRef.current.delete(cacheKey);
            }
        })();

        sceneGroupIdsPendingRef.current.set(cacheKey, queryPromise);
        return queryPromise;
    }, [idPath, getCurrentLanguage]);

    useEffect(() => {
        sceneGroupIdsCacheRef.current.clear();
        sceneGroupIdsPendingRef.current.clear();
    }, [idPath, locale]);

    const isXmlUrl = useCallback((url: string): boolean => {
        if (!url) return false;
        try {
            const parsed = /^https?:\/\//i.test(url)
                ? new URL(url)
                : new URL(url, window.location.origin);
            return parsed.pathname.toLowerCase().endsWith('.xml');
        } catch {
            return url.split('?')[0].split('#')[0].toLowerCase().endsWith('.xml');
        }
    }, []);

    const toProxyTileUrl = useCallback((rawUrl: string): string => {
        if (!rawUrl) return rawUrl;

        if (rawUrl.includes('/v1/general/cmc/proxy-tile?fileUrl=')) {
            return rawUrl;
        }

        const apiBase = '/api/proxy';

        return `${apiBase}/v1/general/cmc/proxy-tile?fileUrl=${encodeURIComponent(rawUrl)}`;
    }, []);

    const detectMobileSafariProfile = useCallback(() => {
        if (typeof navigator === 'undefined') {
            return { isIPhone: false, isIOSSafari: false, isAndroid: false };
        }

        const ua = navigator.userAgent || '';
        const isIPhone = /iPhone/i.test(ua);
        const isIOS = /iP(hone|od|ad)/i.test(ua);
        const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
        const isAndroid = /android/i.test(ua);

        return {
            isIPhone,
            isIOSSafari: isIOS && isSafari,
            isAndroid,
        };
    }, []);

    const getMaxZoomLevel = useCallback(() => {
        const { isIPhone, isIOSSafari } = detectMobileSafariProfile();
        if (isIPhone) {
            return MAX_ZOOM_IPHONE;
        }
        if (isIOSSafari) {
            return MAX_ZOOM_IOS_SAFARI;
        }
        return MAX_ZOOM_DEFAULT;
    }, [detectMobileSafariProfile]);

        const toProxyImageUrl = useCallback((rawUrl: string, isMapImage: boolean = false): string => {
            if (!rawUrl) return rawUrl;

            // Prevent double wrapping.
            if (rawUrl.includes('/v1/general/cmc/proxy-image')) {
                return rawUrl;
            }

            // Do not proxy KRPano XML configs
            if (isXmlUrl(rawUrl)) {
                return rawUrl;
            }

            // Relative paths should stay local.
            if (!/^https?:\/\//i.test(rawUrl)) {
                return rawUrl;
            }

            // URLs matching the local origin must stay local and not be proxied.
            if (typeof window !== 'undefined' && rawUrl.startsWith(window.location.origin)) {
                const pathOnly = rawUrl.slice(window.location.origin.length);
                return pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
            }

            const apiBase = '/api/proxy';

            // For map images, if it is a dynamic tile URL (Osm proxy) skip proxy
            if (isMapImage && (rawUrl.includes('{z}') || rawUrl.includes('{x}'))) {
                return rawUrl;
            }

            if (isMapImage) {
                // Balance: high enough to stay sharp when zoomed in, low enough to avoid OOM on iPhone.
                // iPhone WebGL texture limit is typically 4096x4096 but decoding a large JPEG spikes
                // memory right when the popup first appears (post-login). We preload the image early
                // (see preloadMapImage below) so the browser has already decoded it by then.
                const { isIPhone, isIOSSafari, isAndroid } = detectMobileSafariProfile();

                const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
                let targetWidth = isMobile ? 2048 : 4096;
                let quality = 80;

                if (isIPhone) {
                    // 1600px keeps detail visible at 4× zoom while staying within iPhone memory budget.
                    // Image is preloaded early so the decode does NOT happen at popup-open time.
                    targetWidth = 1600;
                    quality = 75;
                } else if (isIOSSafari) {
                    targetWidth = 1600;
                    quality = 72;
                } else if (isAndroid) {
                    // Android mid-range devices have weaker GPUs than iPhone;
                    // large image decode spikes cause visible jank when the popup opens.
                    targetWidth = isMobile ? 1600 : 2048;
                    quality = 75;
                }

                return `${apiBase}/v1/general/cmc/proxy-image/?fileUrl=${encodeURIComponent(rawUrl)}&w=${targetWidth}&q=${quality}`;
            }

            return `${apiBase}/v1/general/cmc/proxy-image/?fileUrl=${encodeURIComponent(rawUrl)}`;
        }, [isXmlUrl, detectMobileSafariProfile]);

    /**
     * Preload a map image into the browser cache without blocking the main thread.
     * Uses requestIdleCallback when available so it runs during idle time,
     * meaning the image will already be decoded by the time the popup opens.
     */
    const preloadMapImage = useCallback((rawUrl: string) => {
        if (typeof window === 'undefined' || !rawUrl) return;

        const baseUrl = window.location.origin;
        const absoluteUrl = rawUrl.startsWith('http') ? rawUrl : `${baseUrl}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;

        // Build the exact same proxied URL that KRPano will use later
        const proxyUrl = toProxyImageUrl(absoluteUrl, true);
        if (!proxyUrl) return;

        const doPreload = () => {
            const img = new window.Image();
            img.decoding = 'async';
            img.src = proxyUrl;
            // decode() forces the browser to fully decode the bitmap off-thread
            // so that when KRPano sets layer[map].url, the texture upload is near-instant.
            img.decode?.().catch(() => { /* ignore — preload is best-effort */ });
        };

        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(doPreload, { timeout: 3000 });
        } else {
            setTimeout(doPreload, 800);
        }
    }, [toProxyImageUrl]);

    /**
     * Calculate optimal scale based on container size and image dimensions
     * Using targetElementId to avoid hardcoding specific elements
     */
    const calculateOptimalScale = useCallback(async (): Promise<number> => {
        const defaultScale = window.innerWidth <= 768 ? 0.6 : 0.8;

        const container = document.getElementById(targetElementId);
        if (!container) {
            console.warn(`⚠️ Container element #${targetElementId} not found, using default scale`);
            return defaultScale;
        }

        const containerWidth = container.offsetWidth;
        const containerHeight = container.offsetHeight;
        if (containerWidth === 0 || containerHeight === 0) {
            console.warn('⚠️ Container has no dimensions, using default scale');
            return defaultScale;
        }

        const krpano = krpanoRef.current;
        if (!krpano || typeof krpano.get !== 'function') {
            return defaultScale;
        }

        // Wait for KRPano to report image dimensions after setting map url.
        for (let i = 0; i < 20; i++) {
            const imgWidth = Number(krpano.get('layer[map].imagewidth')) || 0;
            const imgHeight = Number(krpano.get('layer[map].imageheight')) || 0;

            // Ignore obviously invalid early values (e.g. 1x1 during loading)
            if (imgWidth > 32 && imgHeight > 32) {
                const scaleX = containerWidth / imgWidth;
                const scaleY = containerHeight / imgHeight;
                return Math.min(scaleX, scaleY);
            }

            await new Promise((resolve) => setTimeout(resolve, 100));
        }

        return defaultScale;
    }, [targetElementId]);

    /**
     * Load KRPano script if not already loaded
     */
    const loadKrpanoScript = useCallback((): Promise<void> => {
        return new Promise((resolve, reject) => {
            if (typeof window.embedpano === 'function') {
                resolve();
                return;
            }

            try {
                const scriptEl = document.createElement('script');
                scriptEl.src = '/krpano-ver1/krpano.js';
                scriptEl.async = true;

                let resolved = false;

                scriptEl.onload = () => {
                    if (!resolved) {
                        resolved = true;
                        resolve();
                    }
                };

                scriptEl.onerror = (error) => {
                    if (!resolved) {
                        resolved = true;
                        reject(new Error('Failed to load Krpano script'));
                    }
                };

                if (document.head) {
                    document.head.appendChild(scriptEl);
                } else if (document.body) {
                    document.body.appendChild(scriptEl);
                } else {
                    throw new Error('No suitable parent element found for script insertion');
                }

                // Backup timeout
                setTimeout(() => {
                    if (!resolved) {
                        resolved = true;
                        if (typeof window.embedpano === 'function') {
                            resolve();
                        } else {
                            reject(new Error('Krpano script loading timeout'));
                        }
                    }
                }, 8000);
            } catch (err) {
                reject(new Error(`Failed to load Krpano: ${err instanceof Error ? err.message : String(err)}`));
            }
        });
    }, []);

    /**
     * Applies styling, URL, crop, scale, zorder, edge and tooltip to an existing Krpano marker/hotspot
     */
    const applyMarkerPresentation = useCallback(
        (
            krpano: any,
            item: EnhancedProcessedLocation,
            isActive: boolean,
            isTileMode: boolean,
            markerDisplayOverride?: unknown
        ) => {
            const layerName = `marker_${item.id}`;
            const elementType: 'hotspot' | 'layer' = isTileMode ? 'hotspot' : 'layer';

            const mapMarkerDisplayToUse = markerDisplayOverride !== undefined
                ? markerDisplayOverride
                : (currentMarkerDisplayRef.current ?? options.mapMarkerDisplay);

            const fallbackNumber = (item as any).display_number ?? (item as any).number ?? (item as any).index ?? item.id;
            const markerPresentation = resolveMarkerPresentation({
                mapMarkerDisplayRaw: mapMarkerDisplayToUse,
                locationConfigRaw: item.config,
                isActive,
                fallbackNumber,
            });

            const isNumberedBadge = markerPresentation.isNumbered;
            const targetEdge = isNumberedBadge ? 'center' : 'bottomcenter';
            const activeScale = markerPresentation.activeScale ?? DEFAULT_HOTSPOT_ACTIVE_SCALE;

            let baseScale = 1.0;
            const intervalKey = `${elementType}_${layerName}`;
            if (activeMarkerIntervalsRef.current[intervalKey]) {
                clearInterval(activeMarkerIntervalsRef.current[intervalKey]);
                delete activeMarkerIntervalsRef.current[intervalKey];
            }

            if (isNumberedBadge) {
                const rawLabel = markerPresentation.label || String(fallbackNumber || 1);

                const badgeDataUrl = generateNumberedBadgeSvgDataUrl({
                    label: rawLabel,
                    border: markerPresentation.border,
                    fill: markerPresentation.fill,
                    text: markerPresentation.text,
                    size: 32,
                });

                const currentBadgeUrl = krpano.get(`${elementType}[${layerName}].url`);
                if (currentBadgeUrl !== badgeDataUrl) {
                    krpano.set(`${elementType}[${layerName}].url`, badgeDataUrl);
                }
                const normalScale = typeof item.scale === 'number' && item.scale > 0 ? item.scale : 1.0;
                baseScale = normalScale;
                krpano.set(`${elementType}[${layerName}].scale`, baseScale);
                krpano.set(`${elementType}[${layerName}].crop`, '');
                try {
                    const sprite = krpano.get(`${elementType}[${layerName}].sprite`) as HTMLElement | null;
                    if (sprite && sprite.style) {
                        sprite.style.filter = '';
                        if (!sprite.style.backgroundImage || sprite.style.backgroundImage === 'none') {
                            sprite.style.backgroundImage = `url("${badgeDataUrl}")`;
                            sprite.style.backgroundSize = '32px 32px';
                            sprite.style.backgroundRepeat = 'no-repeat';
                        }
                    }
                } catch {}
                const initialZorder = isActive ? 20 : 10;
                krpano.set(`${elementType}[${layerName}].zorder`, initialZorder);
                markerBaseZordersRef.current[layerName] = initialZorder;
            } else {
                const normalIconUrl = resolveNormalIconUrl(item.icon, toProxyImageUrl);

                const locConfig = parseLocationConfig(item.config);
                const isGif = /\.gif(?:$|[?#])/i.test(item.icon || '');
                const looksLikeSpritePath = /(sprite|sheet|sequence|atlas|strip)/i.test(item.icon || '');
                const isExplicitSprite = Boolean(
                    locConfig.is_sprite || locConfig.isSprite || locConfig.is_animated ||
                    (item as any).is_sprite || (item as any).isSprite
                );
                const isSprite = !isGif && (looksLikeSpritePath || isExplicitSprite);

                const normalScale = typeof item.scale === 'number' && item.scale > 0
                    ? item.scale
                    : (isSprite ? 1.0 : 0.5);
                baseScale = normalScale;

                const activeBorder = markerPresentation.active?.border || DEFAULT_NUMBERED_PALETTE.active.border;
                const activeFill = markerPresentation.active?.fill || DEFAULT_NUMBERED_PALETTE.active.fill;
                const circleSize = 75; // Tăng đường kính active circle legacy hotspot khoảng 1,5 lần (50px * 1.5 = 75px)
                const circleDataUrl = generateNumberedBadgeSvgDataUrl({
                    label: '',
                    border: activeBorder,
                    fill: activeFill,
                    text: '',
                    size: circleSize,
                });

                // User request: active circle phải THAY THẾ biểu tượng hotspot.
                // Hotspot active: chỉ hiện hình tròn xanh viền trắng, ẩn hoàn toàn icon gốc (ghim đỏ hoặc icon-blue).
                // Hotspot hết active: khôi phục icon gốc.
                const finalIconUrl = isActive ? circleDataUrl : normalIconUrl;
                const currentIconUrl = krpano.get(`${elementType}[${layerName}].url`);
                if (currentIconUrl !== finalIconUrl) {
                    krpano.set(`${elementType}[${layerName}].url`, finalIconUrl);
                }
                if (isActive) {
                    krpano.set(`${elementType}[${layerName}].scale`, baseScale);
                    krpano.set(`${elementType}[${layerName}].crop`, '');
                } else if (isSprite) {
                    krpano.set(`${elementType}[${layerName}].scale`, baseScale);
                    krpano.set(`${elementType}[${layerName}].crop`, '0|0|200|200');
                    krpano.set(`${elementType}[${layerName}].framewidth`, 200);
                    krpano.set(`${elementType}[${layerName}].frameheight`, 200);
                    krpano.set(`${elementType}[${layerName}].frame`, 0);
                    krpano.set(`${elementType}[${layerName}].lastframe`, 24);
                } else {
                    krpano.set(`${elementType}[${layerName}].scale`, baseScale);
                    krpano.set(`${elementType}[${layerName}].crop`, '');
                }

                const initialZorder = isActive ? 20 : 10;
                krpano.set(`${elementType}[${layerName}].zorder`, initialZorder);
                markerBaseZordersRef.current[layerName] = initialZorder;
            }

            const circleLayerName = `${layerName}__active_circle`;
            const existingCircle = krpano.get(`${elementType}[${circleLayerName}].name`);
            if (existingCircle) {
                try {
                    krpano.call(`remove${elementType}(${circleLayerName})`);
                } catch {}
            }

            markerBaseScalesRef.current[layerName] = baseScale;
            krpano.set(`${elementType}[${layerName}].edge`, targetEdge);
            krpano.set(`${elementType}[${layerName}].visible`, true);
            krpano.set(`${elementType}[${layerName}].alpha`, 1.0);

            const resolvedTitle = resolveMarkerTitleForLocale(item);
            if (resolvedTitle) {
                krpano.set(`${elementType}[${layerName}].tooltip`, resolvedTitle);
                markerTitlesRef.current[layerName] = resolvedTitle;
            }

            return { isNumberedBadge, targetEdge };
        },
        [options.mapMarkerDisplay, toProxyImageUrl, resolveMarkerTitleForLocale]
    );

    /**
     * Resolves group scene highlight in the background without blocking initial rendering.
     */
    const resolveGroupsInBackground = useCallback(
        (targetScene: string | undefined, targetMapId: number | string | undefined, targetKrpano: any, candidateMarkers: EnhancedProcessedLocation[]) => {
            if (!targetScene || isMapForAdmin || !candidateMarkers || candidateMarkers.length === 0) return;

            (async () => {
                try {
                    const currentSceneGroupIds = await getSceneGroupIdsBySceneName(targetScene);
                    if (currentSceneRef.current !== targetScene || krpanoRef.current !== targetKrpano || (targetMapId && minimapDataRef.current?.id && minimapDataRef.current?.id !== targetMapId)) {
                        return;
                    }

                    const currentGroupIdSet = new Set(currentSceneGroupIds);
                    const groupMatchedMarkerIds = new Set<string>();

                    if (currentGroupIdSet.size > 0) {
                        await Promise.all(candidateMarkers.map(async (marker) => {
                            // A group filters scene choices in Admin; a saved scene remains the active target.
                            if (hasLocationSceneTarget(marker)) return;
                            const markerConfig = parseMarkerConfigObject(marker.config);
                            const configGroupId = String(markerConfig.scene_group_id || '').trim();
                            if (configGroupId && currentGroupIdSet.has(configGroupId)) {
                                groupMatchedMarkerIds.add(String(marker.id));
                                return;
                            }

                            const markerSceneRef = String(marker.ref || '').trim();
                            if (!markerSceneRef) return;

                            const markerSceneGroupIds = await getSceneGroupIdsBySceneName(markerSceneRef);
                            if (currentSceneRef.current !== targetScene || krpanoRef.current !== targetKrpano) {
                                return;
                            }

                            if (markerSceneGroupIds.some((gId) => currentGroupIdSet.has(gId))) {
                                groupMatchedMarkerIds.add(String(marker.id));
                            }
                        }));
                    }

                    if (currentSceneRef.current !== targetScene || krpanoRef.current !== targetKrpano || (targetMapId && minimapDataRef.current?.id && minimapDataRef.current?.id !== targetMapId)) {
                        return;
                    }

                    const mapMarkerDisplayToUse = currentMarkerDisplayRef.current ?? options.mapMarkerDisplay;
                    const isMapVis = targetKrpano.get('layer[map].visible');
                    const isTileMode = isMapVis !== true && isMapVis !== 'true' && isMapVis !== 1 && isMapVis !== '1';

                    for (const item of candidateMarkers) {
                        const isCurrentSceneDirect = Boolean(targetScene && isLocationMatchingScene(item, targetScene));
                        const isExplicitlySelected = selectedLocationIdRef.current !== null && selectedLocationIdRef.current !== undefined && String(selectedLocationIdRef.current) === String(item.id);
                        const isGroupActive = groupMatchedMarkerIds.has(String(item.id));
                        const isSceneActive = isCurrentSceneDirect || isGroupActive;
                        const hasExplicitSelection = selectedLocationIdRef.current !== null && selectedLocationIdRef.current !== undefined && selectedLocationIdRef.current !== '';
                        const isActive = hasExplicitSelection ? isExplicitlySelected : isSceneActive;

                        applyMarkerPresentation(targetKrpano, item, isActive, isTileMode, mapMarkerDisplayToUse);
                    }
                } catch (groupErr) {
                    console.warn('⚠️ Background group resolution failed:', groupErr);
                }
            })();
        },
        [isMapForAdmin, getSceneGroupIdsBySceneName, applyMarkerPresentation, options.mapMarkerDisplay]
    );

    /**
     * Create a single marker on the KRPano instance
     */
    const createMarker = useCallback(
        async (item: EnhancedProcessedLocation, markerDisplayOverride?: unknown): Promise<boolean> => {
            const krpano = krpanoRef.current;
            if (!krpano || typeof krpano.call !== 'function' || typeof krpano.set !== 'function') {
                console.warn('⚠️ Krpano instance is invalid');
                return false;
            }

            const layerName = `marker_${item.id}`;
            let lastExecutedStep = 'init';

            try {
                lastExecutedStep = 'check_isTileMode';
                const isMapVis = krpano.get('layer[map].visible');
                const isTileMode = isMapVis !== true && isMapVis !== 'true' && isMapVis !== 1 && isMapVis !== '1';
                const elementType = isTileMode ? 'hotspot' : 'layer';

                lastExecutedStep = `add${elementType}(${layerName})`;
                krpano.call(`add${elementType}(${layerName})`);

                if (!isTileMode) {
                    lastExecutedStep = `set parent=map`;
                    krpano.set(`layer[${layerName}].parent`, 'map');
                }

                // Check if this marker corresponds to the current scene directly or by selection
                const isCurrentSceneDirect = Boolean(currentSceneRef.current && isLocationMatchingScene(item, currentSceneRef.current));
                const isSceneGroupDirect = !hasLocationSceneTarget(item) && Boolean(item._highlightCurrentByGroup);
                const isCurrentLocation = isCurrentSceneDirect || isSceneGroupDirect;
                const isExplicitlySelected = selectedLocationIdRef.current !== null && selectedLocationIdRef.current !== undefined && String(selectedLocationIdRef.current) === String(item.id);
                const hasExplicitSelection = selectedLocationIdRef.current !== null && selectedLocationIdRef.current !== undefined && selectedLocationIdRef.current !== '';
                const isActive = hasExplicitSelection ? isExplicitlySelected : isCurrentLocation;

                lastExecutedStep = 'applyMarkerPresentation';
                const { isNumberedBadge, targetEdge } = applyMarkerPresentation(krpano, item, isActive, isTileMode, markerDisplayOverride);

                if (isTileMode) {
                    const xNum = Number(item.x);
                    const yNum = Number(item.y);
                    const isPercent = xNum <= 100 && yNum <= 100;
                    let ath = 0, atv = 0;
                    const hfov = Number(krpano.get('image.hfov')) || 1.0;
                    
                    if (hfov === 1.0) {
                        if (isPercent) {
                            ath = (xNum / 100) - 0.5;
                            const imgW = Number(krpano.get('image.width')) || 1;
                            const imgH = Number(krpano.get('image.height')) || 1;
                            const ratio = imgH / imgW;
                            atv = ((yNum / 100) - 0.5) * ratio;
                        } else {
                            const imgW = Number(krpano.get('image.width')) || 1;
                            const imgH = Number(krpano.get('image.height')) || 1;
                            ath = (xNum / imgW) - 0.5;
                            atv = (yNum / imgH) - 0.5;
                        }
                    } else {
                        const imgW = Number(krpano.get('image.width')) || 1000;
                        const imgH = Number(krpano.get('image.height')) || 1000;
                        if (isPercent) {
                            ath = (xNum / 100) * imgW - (imgW / 2);
                            atv = (yNum / 100) * imgH - (imgH / 2);
                        } else {
                            ath = xNum - (imgW / 2);
                            atv = yNum - (imgH / 2);
                        }
                    }
                    krpano.set(`${elementType}[${layerName}].ath`, ath);
                    krpano.set(`${elementType}[${layerName}].atv`, atv);
                    krpano.set(`${elementType}[${layerName}].edge`, targetEdge);
                } else {
                    krpano.set(`layer[${layerName}].align`, 'lefttop');
                    krpano.set(`layer[${layerName}].edge`, targetEdge);
                    if (Number(item.x) > 100 || Number(item.y) > 100) {
                        krpano.set(`layer[${layerName}].x`, item.x);
                        krpano.set(`layer[${layerName}].y`, item.y);
                    } else {
                        krpano.set(`layer[${layerName}].x`, `${item.x}%`);
                        krpano.set(`layer[${layerName}].y`, `${item.y}%`);
                    }
                }

                krpano.set(`${elementType}[${layerName}].visible`, true);
                krpano.set(`${elementType}[${layerName}].enabled`, true);
                krpano.set(`${elementType}[${layerName}].capture`, true);
                krpano.set(`${elementType}[${layerName}].handcursor`, true);
                krpano.set(`${elementType}[${layerName}].alpha`, 1.0);
                const resolvedTitle = resolveMarkerTitleForLocale(item);
                krpano.set(`${elementType}[${layerName}].tooltip`, resolvedTitle);

                // Hover tooltip overlay & two-way hover synchronization
                const safeId = (targetElementId || DEFAULT_TARGET_ID).replace(/[^a-zA-Z0-9_]/g, '_');
                const tooltipText = resolvedTitle;
                if (tooltipText) {
                    markerTitlesRef.current[layerName] = tooltipText;
                }
                krpano.set(`${elementType}[${layerName}].onover`, `js(window.__mmShow_${safeId}('${layerName}', ${item.id}));`);
                krpano.set(`${elementType}[${layerName}].onout`, `js(window.__mmHide_${safeId}('${layerName}', ${item.id}));`);

                // Set click actions based on type
                let markerAction = '';
                if (isMapForAdmin) {
                    markerAction = `js(window.onclickMarkerInAdmin(${item.id}));`;
                } else {
                    let navigate: () => void = () => {};
                    const image2dUrls = parseImage2DUrlsFromConfig(item.config, item.entity_id);
                    // Explicit actions take precedence over legacy image URL inference.
                    const hasExplicitAction = ['scene', 'view', 'map', 'link', 'function'].includes(item.type);
                    const isImage2DMarker = item.type === 'image2d' || (!hasExplicitAction && image2dUrls.length > 0);

                    if (isImage2DMarker) {
                        const markerTitle = resolveMarkerTitleForLocale(item) || 'Hinh anh 2D';

                        image2dMarkerDataRef.current[String(item.id)] = {
                            title: markerTitle,
                            urls: image2dUrls,
                            location: item,
                        };

                        navigate = () => window.changeViewByMarkerHandlers?.[targetElementId]?.(String(item.id), 'image2d');
                    } else if (item.type === 'map') {
                        // Use instance-specific handler to avoid conflicts between multiple minimap instances
                        navigate = () => window.changeMapHandlers?.[targetElementId]?.(Number(item.entity_id));
                    } else if (item.type === 'scene' || item.type === 'view') {
                        navigate = () => window.changeViewByMarkerHandlers?.[targetElementId]?.(String(item.ref || item.entity_id || ''), item.type, item.id);
                    } else if (item.type === 'link') {
                        navigate = () => window.changeViewByMarkerHandlers?.[targetElementId]?.(String(item.entity_id), 'link');
                    } else if (item.type === 'function') {
                        const rawRef = String(item.ref ?? '').trim().replace(/;+$/, '');
                        if (rawRef) {
                            const isSafeExpression = /^[a-zA-Z0-9_\$.]+(?:\([a-zA-Z0-9_\$.(),\s]*\))?$/.test(rawRef);
                            if (isSafeExpression) {
                                if (/\(.*\)$/.test(rawRef)) {
                                    navigate = () => krpano.call(`js(${rawRef});`);
                                } else if (rawRef.includes('.')) {
                                    navigate = () => krpano.call(`js(typeof ${rawRef} === 'function' ? ${rawRef}() : undefined);`);
                                } else {
                                    navigate = () => krpano.call(`js(typeof window['${rawRef}'] === 'function' ? window['${rawRef}']() : undefined);`);
                                }
                            }
                        }
                    }

                    markerClickHandlersRef.current[String(item.id)] = () => {
                        onMarkerSelectRef.current?.(item.id);
                        navigate();
                    };
                    // Match the simple function-call bridge used by onover/onout.
                    markerAction = `js(window.__mmClick_${safeId}(${item.id}));`;
                }

                if (markerAction) {
                    krpano.set(`${elementType}[${layerName}].onclick`, markerAction);
                }

                return true;
            } catch (err: any) {
                const errorInfo = {
                    markerId: item.id,
                    layerName,
                    lastExecutedStep,
                    errorMessage: err?.message || String(err),
                    errorStack: err?.stack || '',
                };
                console.error(`❌ Error creating marker ${layerName} at step "${lastExecutedStep}":`, err);
                try {
                    krpano.call(`removelayer(${layerName})`);
                    krpano.call(`removelayer(${layerName}__active_circle)`);
                } catch (removeErr) {
                    // Ignore cleanup errors
                }
                try {
                    krpano.call(`removehotspot(${layerName})`);
                    krpano.call(`removehotspot(${layerName}__active_circle)`);
                } catch (removeErr) {
                    // Ignore cleanup errors
                }
                return false;
            }
        },
        [isMapForAdmin, targetElementId, locale, applyMarkerPresentation]
    );

    /**
     * Display map and markers
     */
    const displayMapAndMarkers = useCallback(
        async (mapData: ProcessedMinimap) => {
            const fitGeneration = ++mapFitGenerationRef.current;
            // Wait for krpano to be ready
            let attempts = 0;
            const maxAttempts = 10;
            while (attempts < maxAttempts) {
                const krpano = krpanoRef.current;
                if (krpano && typeof krpano.call === 'function' && typeof krpano.set === 'function') {
                    break;
                }
                attempts++;
                await new Promise((resolve) => setTimeout(resolve, 200));
            }

            const krpano = krpanoRef.current;
            if (!krpano) {
                console.error('❌ Krpano instance not available after waiting');
                setError('timeout');
                return;
            }

            try {
                clearHotspotIntervals();

                // Clear existing markers
                try {
                    const allLayers = Number(krpano.get('layer.count') || 0);
                    for (let i = allLayers - 1; i >= 0; i--) {
                        const layerName = String(krpano.get(`layer[${i}].name`) || '');
                        if (layerName && layerName.startsWith('marker_')) {
                            krpano.call(`removelayer(${layerName});`);
                        }
                    }
                } catch (cleanupError) {
                    console.warn('⚠️ Error during marker cleanup:', cleanupError);
                }

                krpano.call('removeall');
                krpano.call('action(hide_hover_image_minimap);');

                setLocations([]);
                markerTitlesRef.current = {};
                markerClickHandlersRef.current = {};
                markerBaseScalesRef.current = {};
                markerBaseZordersRef.current = {};
                currentlyHoveredMarkerRef.current = null;
                image2dMarkerDataRef.current = {};
                setHoverTooltip({ text: '', visible: false });

                // Build map URL
                const baseUrl = window.location.origin;
                const rawMapUrl = mapData.url_map.startsWith('http') ? mapData.url_map : `${baseUrl}${mapData.url_map.startsWith('/') ? '' : '/'}${mapData.url_map}`;
                const mapUrl = toProxyImageUrl(rawMapUrl, true);

                const isXmlMap = isXmlUrl(rawMapUrl);
                const isOsmMap = rawMapUrl.includes('{z}') && rawMapUrl.includes('{x}') && rawMapUrl.includes('{y}');
                const useTileMode = isXmlMap || isOsmMap;

                if (useTileMode) {
                    // Hide legacy map layer
                    try {
                        krpano.set('layer[map].visible', false);
                    } catch (e) {}

                    if (isXmlMap) {
                        const xmlProxyUrl = toProxyTileUrl(rawMapUrl);
                        krpano.call(`loadpano('${xmlProxyUrl}', null, MERGE, BLEND(0.5));`);
                    } else if (isOsmMap) {
                        const osmXml = `
                            <krpano>
                                <scene name="osm_map_${mapData.id}" autoload="true">
                                    <view limitview="range" maxpixelzoom="2" hfov="1" />
                                    <image maptype="osm" hfov="0.001">
                                        <flat url="${rawMapUrl}" multires="256,512x512,1024x1024,2048x2048,4096x4096,8192x8192,16384x16384,32768x32768,65536x65536" />
                                    </image>
                                </scene>
                            </krpano>
                        `;
                        krpano.call(`loadxml('${osmXml}', null, MERGE, BLEND(0.5));`);
                    }
                    
                    // Wait for the tile scene to be parsed and loaded
                    await new Promise((resolve) => setTimeout(resolve, 500));
                } else {
                    // Reset map layer XML-defined event handlers and properties.
                    try {
                        krpano.set('layer[map].onloaded', '');
                        krpano.set('layer[map].onclick', '');
                        krpano.set('layer[map].scalechildren', false);
                        krpano.set('layer[map].align', 'center');
                        krpano.set('layer[map].edge', 'center');
                        krpano.call('layer[map].changeorigin(center, center);');
                        krpano.call('stoptween(layer[map].scale);');
                        krpano.call('stoptween(layer[map].x);');
                        krpano.call('stoptween(layer[map].y);');
                        krpano.set('layer[map].x', 0);
                        krpano.set('layer[map].y', 0);
                    } catch (resetErr) {
                        console.warn('⚠️ Could not reset map layer handlers:', resetErr);
                    }

                    // Set map properties
                    krpano.set('layer[map].url', mapUrl);
                    krpano.set('layer[map].visible', true);

                    const fallbackScale = window.innerWidth <= 768 ? 0.6 : 0.8;
                    const desktopScale = Number(mapData.scale);
                    const mobileScale = Number(mapData.scale_mobile);
                    const preferredScale = window.innerWidth <= 768 && Number.isFinite(mobileScale) && mobileScale > 0
                        ? mobileScale
                        : desktopScale;
                    const configuredScale = Number.isFinite(preferredScale) && preferredScale > 0
                        ? Math.min(preferredScale, getMaxZoomLevel())
                        : null;
                    krpano.set('layer[map].scale', configuredScale ?? fallbackScale);

                    const optimalScale = configuredScale ?? await calculateOptimalScale();
                    if (mapFitGenerationRef.current !== fitGeneration || krpanoRef.current !== krpano) return;
                    const safeInitialScale = configuredScale ?? (Number.isFinite(optimalScale)
                        ? Math.min(Math.max(optimalScale, MIN_INITIAL_SCALE), Math.min(MAX_INITIAL_SCALE, fallbackScale))
                        : fallbackScale);
                    krpano.set('layer[map].scale', safeInitialScale);
                    krpano.set('layer[map].x', 0);
                    krpano.set('layer[map].y', 0);

                    baseScaleRef.current = safeInitialScale;

                    const { isIPhone } = detectMobileSafariProfile();
                    if (configuredScale === null && !isIPhone) {
                        setTimeout(async () => {
                            try {
                                const activeKrpano = krpanoRef.current;
                                if (mapFitGenerationRef.current !== fitGeneration || activeKrpano !== krpano ||
                                    !activeKrpano || typeof activeKrpano.get !== 'function' || typeof activeKrpano.set !== 'function') {
                                    return;
                                }

                                const refinedScaleRaw = await calculateOptimalScale();
                                // A previous map's fit must not override a new map or user zoom/pan.
                                if (!Number.isFinite(refinedScaleRaw) || mapFitGenerationRef.current !== fitGeneration ||
                                    krpanoRef.current !== activeKrpano ||
                                    Number(activeKrpano.get('layer[map].scale')) !== safeInitialScale ||
                                    Number(activeKrpano.get('layer[map].x')) !== 0 ||
                                    Number(activeKrpano.get('layer[map].y')) !== 0) return;

                                const refinedScale = Math.min(Math.max(refinedScaleRaw, MIN_INITIAL_SCALE), Math.min(MAX_INITIAL_SCALE, fallbackScale));
                                activeKrpano.set('layer[map].scale', refinedScale);
                                activeKrpano.set('layer[map].x', 0);
                                activeKrpano.set('layer[map].y', 0);
                                baseScaleRef.current = refinedScale;
                            } catch (fitErr) {
                                console.warn('⚠️ Delayed map fit failed:', fitErr);
                            }
                        }, 380);
                    }
                }

                // Fetch markers
                const currentLanguage = getCurrentLanguage();
                const markerDataResponse = await minimapServices.getMapAndLocation(idPath, mapData.id, currentLanguage, accessToken);
                if (mapFitGenerationRef.current !== fitGeneration || krpanoRef.current !== krpano) {
                    return;
                }
                const markerData = markerDataResponse?.data?.locations;
                const activeMarkerDisplay = markerDataResponse?.data?.marker_display || (mapData as any)?.marker_display || options.mapMarkerDisplay;
                if (activeMarkerDisplay) {
                    currentMarkerDisplayRef.current = activeMarkerDisplay;
                }

                const fullMapData = {
                    ...mapData,
                    marker_display: activeMarkerDisplay,
                };

                setMinimapData(fullMapData);
                minimapDataRef.current = fullMapData;
                mapHistory.push({ id: mapData.id, name: mapData.title, map: mapData.url_map });

                if (!Array.isArray(markerData)) {
                    console.warn('⚠️ Invalid marker data received');
                    setLocations([]);
                    return;
                }

                // 1. Tạo marker NGAY LẬP TỨC: Xác định active trực tiếp bằng scene/selection trước (Zero-delay)
                if (markerData.length > 0) {
                    const krpanoInstance = krpanoRef.current;
                    if (krpanoInstance && typeof krpanoInstance.call === 'function') {
                        const markerPromises = markerData.map((marker) => createMarker(marker, activeMarkerDisplay));
                        await Promise.all(markerPromises);
                    }
                }

                if (mapFitGenerationRef.current !== fitGeneration || krpanoRef.current !== krpano) {
                    return;
                }

                setLocations(markerData);
                onLocationsLoadedRef.current?.(markerData);

                // Kích hoạt tra nhóm scene ở nền không chặn luồng hiển thị
                const candidateGroupLinkedMarkers = markerData.filter((marker: EnhancedProcessedLocation) => {
                    if (marker.type !== 'scene' && marker.type !== 'view') return false;
                    const markerConfig = parseMarkerConfigObject(marker.config);
                    return markerConfig.scene_link_type === 'group_scene';
                });
                resolveGroupsInBackground(currentSceneRef.current, mapData.id, krpano, candidateGroupLinkedMarkers);
            } catch (err) {
                console.error('❌ Error displaying map and markers:', err);
                setError(`Lỗi khi hiển thị bản đồ: ${err instanceof Error ? err.message : String(err)}`);
            } finally {
                setIsLoading(false);
            }
        },
        [idPath, createMarker, mapHistory, getCurrentLanguage, calculateOptimalScale, toProxyImageUrl, isXmlUrl, toProxyTileUrl, detectMobileSafariProfile, getMaxZoomLevel, accessToken, clearHotspotIntervals, isMapForAdmin, resolveGroupsInBackground]
    );

    /**
     * Main initialization function
     */
    const initialize = useCallback(async () => {
        // Debounce check
        const now = Date.now();
        if (now - lastInitTime.current < 1000) {
            return;
        }

        lastInitTime.current = now;
        initializationRef.current = true;
        setIsInitialized(true);
        setError(null);
        setIsLoading(true);

        try {
            // Cleanup existing instance
            if (document.getElementById(krpanoPanoId)) {
                try {
                    syncActiveRef.current = false;
                    clearHotspotIntervals();
                    forceLoseWebGLContext();

                    if (krpanoRef.current && typeof krpanoRef.current.call === 'function') {
                        krpanoRef.current.call('removeall');
                    }
                    if (typeof window.removepano === 'function') {
                        window.removepano(krpanoPanoId);
                    }
                } catch (cleanupError) {
                    console.warn('Warning during cleanup:', cleanupError);
                }

                krpanoRef.current = null;
                setIsReady(false);
                await new Promise((resolve) => setTimeout(resolve, 300));
            }

            // Ensure target element exists
            let targetElement = document.getElementById(targetElementId);
            if (!targetElement) {
                console.warn(`⚠️ Target element #${targetElementId} not found, attempting to create it`);
                const container = document.querySelector('.minimap-container') || document.body;
                if (container) {
                    targetElement = document.createElement('div');
                    targetElement.id = targetElementId;
                    targetElement.style.width = '100%';
                    targetElement.style.height = '100%';
                    container.appendChild(targetElement);
                } else {
                    setError('Không thể tìm thấy phần tử để hiển thị bản đồ');
                    setIsLoading(false);
                    return;
                }
            }

            // Load script and fetch map data
            const [scriptsResult] = await Promise.allSettled([loadKrpanoScript()]);

            if (scriptsResult.status === 'rejected') {
                setError('Không thể tải tập lệnh bản đồ');
                setIsLoading(false);
                return;
            }

            // Fetch map data. If a stale mapId is provided, gracefully fall back to main map.
            let selectedMapResult: any = null;

            if (mapId) {
                const mapByIdResponse: any = await minimapServices.getMinimapById(idPath, mapId, getCurrentLanguage(), accessToken);
                if (mapByIdResponse?.success && mapByIdResponse?.data) {
                    selectedMapResult = mapByIdResponse.data;
                } else {
                    console.warn(`Requested minimap id ${mapId} not found. Falling back to main map.`);
                    const fallbackMainResponse: any = await minimapServices.getMainMap(idPath, getCurrentLanguage(), accessToken);
                    selectedMapResult = fallbackMainResponse?.data?.[0] || null;
                }
            } else {
                const mainMapResponse: any = await minimapServices.getMainMap(idPath, getCurrentLanguage(), accessToken);
                selectedMapResult = mainMapResponse?.data?.[0] || null;
            }

            if (!selectedMapResult) {
                setNoMapData(true);
                setIsLoading(false);
                return;
            }

            // Default marker types
            const markerTypesData: MarkerType[] = [
                {
                    id: 'default',
                    name: 'Default Marker',
                    marker_icon: 'upload/image/minimap/icon-minimap/icon-default.svg',
                    scale: 0.8,
                    style: 'hotspot_minimap-icon_style',
                },
            ];
            setMarkerTypes(markerTypesData);

            const initialMapData = selectedMapResult;
            if (!initialMapData.id || !initialMapData.url_map) {
                setError('Dữ liệu bản đồ không đầy đủ');
                setNoMapData(true);
                setIsLoading(false);
                return;
            }

            setNoMapData(false);

            // Embed KRPano
            let initializationTimeout: NodeJS.Timeout | null = null;
            let resolver: (value: KrpanoInterface) => void;
            let rejector: (reason: Error) => void;

            const initializationPromise = new Promise<KrpanoInterface>((resolve, reject) => {
                resolver = resolve;
                rejector = reject;
            });

            const safeResolve = (krpano: KrpanoInterface) => {
                try {
                    resolver?.(krpano);
                } catch (e) {
                    console.error('Error in promise resolution:', e);
                }
            };

            const safeReject = (err: Error) => {
                try {
                    rejector?.(err);
                } catch (e) {
                    console.error('Error in promise rejection:', e);
                }
            };

            const onKrpanoReady = (krpano_interface: KrpanoInterface) => {
                if (initializationTimeout) {
                    clearTimeout(initializationTimeout);
                    initializationTimeout = null;
                }

                if (!krpano_interface || typeof krpano_interface.call !== 'function') {
                    safeReject(new Error('Invalid Krpano interface received'));
                    return;
                }

                try {
                    krpano_interface.call("trace('Krpano interface test successful')");
                } catch (testError) {
                    console.error('❌ Krpano interface test failed:', testError);
                    return;
                }

                safeResolve(krpano_interface);
            };

            const onKrpanoError = (err: string) => {
                console.error('❌ KRPano embed error:', err);
                if (initializationTimeout) {
                    clearTimeout(initializationTimeout);
                    initializationTimeout = null;
                }

                setError('Bản đồ có thể không hoạt động đầy đủ chức năng');
                setNoMapData(true);
                onErrorRef.current?.(err);

                // Provide minimal interface
                const minimalInterface = {
                    call: (cmd: string) => {
                        console.warn(`⚠️ Attempted to call: ${cmd}`);
                        return null;
                    },
                    get: (name: string) => {
                        console.warn(`⚠️ Attempted to get: ${name}`);
                        return null;
                    },
                    set: (name: string, value: unknown) => {
                        console.warn(`⚠️ Attempted to set: ${name}=${value}`);
                        return false;
                    },
                } as unknown as KrpanoInterface;

                safeResolve(minimalInterface);
            };

            initializationTimeout = setTimeout(() => {
                console.warn('⏱️ Krpano initialization timed out after 8 seconds');
            }, 8000);

            if (typeof window.embedpano !== 'function') {
                await loadKrpanoScript();
                if (typeof window.embedpano !== 'function') {
                    setError('Không thể khởi tạo bản đồ');
                    setIsLoading(false);
                    return;
                }
            }

            try {
                window.embedpano({
                    xml: xmlUrl,
                    id: krpanoPanoId,
                    target: targetElementId,
                    html5: 'auto',
                    mobilescale: 1.0,
                    passQueryParameters: false,
                    wmode: 'opaque',
                    bgcolor: '#ebedfa',
                    security: 'allowdomain(*);allowlocalaccess(true);allowscript(true);',
                    sameorigin: false,
                    cross: false,
                    onready: (krpano_interface) => {
                        try {
                            // Support for OSM tiles via KRPano customParseTilePath
                            (krpano_interface as any).customParseTilePath = function(path: string, cubeside: string, level: string, h: string, v: string, stereo: string, image: any) {
                                if(path.includes("{x}")) { path = path.split("{x}").join(String(Number(h) - 1)); }
                                if(path.includes("{y}")) { path = path.split("{y}").join(String(Number(v) - 1)); }
                                if(path.includes("{z}")) { path = path.split("{z}").join(String(level)); }
                                return path;
                            };
                        } catch(e) {}
                        onKrpanoReady(krpano_interface);
                    },
                    onerror: (err) => {
                        onKrpanoError(err);
                    },
                });
            } catch (embedError) {
                const errorMessage = embedError instanceof Error ? embedError.message : String(embedError);
                onKrpanoError(errorMessage);
            }

            // Wait for initialization
            try {
                const krpano_interface = await initializationPromise;
                krpanoRef.current = krpano_interface;
                setIsReady(true);
                onMapReadyRef.current?.(krpano_interface);

                await new Promise((resolve) => setTimeout(resolve, 300));
                await displayMapAndMarkers(initialMapData);
            } catch (initError) {
                console.error('❌ Could not initialize minimap:', initError);
                setError('Không thể khởi tạo bản đồ');
                setNoMapData(true);
                setIsLoading(false);
            }
        } catch (err) {
            console.error('❌ Error during minimap initialization:', err);
            const errorMessage = err instanceof Error ? err.message : String(err);

            if (errorMessage.includes('timed out')) {
                setError('timeout');
            } else {
                setError(`Lỗi khởi tạo: ${errorMessage}`);
            }
            setNoMapData(true);
            setIsLoading(false);

            krpanoRef.current = null;
            setIsReady(false);
            setIsInitialized(false);
        } finally {
            initializationRef.current = false;
        }
    }, [idPath, mapId, targetElementId, xmlUrl, getCurrentLanguage, loadKrpanoScript, displayMapAndMarkers, krpanoPanoId, accessToken, clearHotspotIntervals, forceLoseWebGLContext]);

    const changeMap = useCallback(
        async (newMapId: number, forceChange: boolean = false) => {
            // Custom handler call
            if (customGroupClickHandler && !forceChange) {
                customGroupClickHandler(newMapId.toString());
                return;
            }

            if (!forceChange && minimapData?.id === newMapId) {
                return;
            }

            if (!isReady) return;

            mapFitGenerationRef.current++;
            clearHotspotIntervals();
            setLocations([]);
            setMapTransform({ scale: 1, x: 0, y: 0, imageWidth: 0, imageHeight: 0 });

            try {
                const krpano = krpanoRef.current;
                // Reset base scale so it gets re-captured for the new map
                baseScaleRef.current = 0;
                if (krpano && typeof krpano.call === 'function') {
                    try {
                        krpano.call('removeall');
                    } catch (cleanupError) {
                        console.warn('⚠️ Error during pre-map-change cleanup:', cleanupError);
                    }
                }

                const mapResult = await minimapServices.getMinimapById(idPath, newMapId.toString(), getCurrentLanguage(), accessToken);
                let data: any = mapResult?.success ? mapResult.data : null;

                if (!data) {
                    const fallbackMainResponse: any = await minimapServices.getMainMap(idPath, getCurrentLanguage(), accessToken);
                    const fallbackMaps: any[] = fallbackMainResponse?.data || [];
                    data = fallbackMaps.find((item: any) => Number(item?.id) === Number(newMapId)) || fallbackMaps[0] || null;

                    if (data) {
                        console.warn(`Map id ${newMapId} not found, switched to fallback map id ${data.id}.`);
                    }
                }

                if (data) {
                    const translation = data.translations?.find((t: { language_id: string }) => t.language_id === getCurrentLanguage()) || data.translations?.[0];
                    
                    const title = translation?.title || data.title || 'Digital Map';
                    const url_map = translation?.url_map || data.url_map;

                    if (url_map) {
                        const newMapData: ProcessedMinimap = {
                            id: data.id,
                            title: title,
                            url_map: url_map,
                            main_map: data.main_map,
                            scale: data.scale,
                            scale_mobile: data.scale_mobile,
                        };
                        await displayMapAndMarkers(newMapData);
                    } else {
                        console.error(`❌ Dữ liệu bản đồ ID ${newMapId} không hợp lệ (thiếu url_map)`);
                        setError(`Dữ liệu bản đồ ID ${newMapId} không hợp lệ`);
                    }
                } else {
                    console.error(`❌ Không tìm thấy dữ liệu cho bản đồ ID ${newMapId}`);
                    setError(`Không tìm thấy dữ liệu cho bản đồ ID ${newMapId}`);
                }
            } catch (err) {
                console.error('❌ Error changing map:', err);
                setError(`Lỗi khi chuyển bản đồ: ${err instanceof Error ? err.message : 'Lỗi không xác định'}`);
            }
        },
        [isReady, idPath, getCurrentLanguage, displayMapAndMarkers, accessToken, customGroupClickHandler, minimapData?.id, clearHotspotIntervals]
    );

    /**
     * Change view by marker (scene navigation)
     */
    const changeViewByMarker = useCallback(
        (sceneId: string, type?: string, locationId?: string | number) => {
            if (type === 'image2d') {
                const cachedMarker = image2dMarkerDataRef.current[String(sceneId)];
                if (cachedMarker && cachedMarker.urls.length > 0) {
                    if (onImage2DMarkerClick) {
                        onImage2DMarkerClick({
                            id: String(sceneId),
                            title: cachedMarker.title,
                            urls: cachedMarker.urls,
                            location: cachedMarker.location,
                        });
                    } else {
                        window.open(cachedMarker.urls[0], '_blank');
                    }
                    return;
                }

                const matchedLocation = locations.find((location) => String(location.id) === String(sceneId));
                if (!matchedLocation) {
                    return;
                }

                const urls = parseImage2DUrlsFromConfig(matchedLocation.config, matchedLocation.entity_id);
                if (urls.length === 0) {
                    return;
                }

                const markerTitle = resolveMarkerTitleForLocale(matchedLocation) || 'Hinh anh 2D';

                if (onImage2DMarkerClick) {
                    onImage2DMarkerClick({
                        id: String(sceneId),
                        title: markerTitle,
                        urls,
                        location: matchedLocation,
                    });
                } else {
                    window.open(urls[0], '_blank');
                }
                return;
            }

            if (type === 'link') {
                window.open(sceneId, '_blank');
                return;
            }

            // Custom handler call
            if (customSceneClickHandler) {
                customSceneClickHandler(sceneId, locationId);
                return;
            }

            onMarkerClick?.();

            const globalWindow = window as unknown as { krpano?: KrpanoInterface };
            const globalKrpano = globalWindow.krpano;

            if (globalKrpano && typeof globalKrpano.call === 'function') {
                try {
                    globalKrpano.call(`loadscene(${sceneId}, null, MERGE, BLEND(1))`);
                    return;
                } catch (err) {
                    console.warn('⚠️ Failed to change scene using global krpano:', err);
                }
            }

            console.warn('⚠️ No available global krpano instance found for scene change');
        },
        [onMarkerClick, customSceneClickHandler, onImage2DMarkerClick, locations, locale]
    );

    /**
     * Go back to previous map in history
     */
    const goBack = useCallback(async () => {
        if (mapHistory.size() > 1) {
            mapHistory.pop();
            const previousMapItem = mapHistory.current();
            if (previousMapItem) {
                await changeMap(previousMapItem.id);
            }
        } else {
            console.warn('⚠️ Không thể quay lại - đã ở map đầu tiên');
        }
    }, [mapHistory, changeMap]);

    /**
     * Retry initialization
     */
    const retry = useCallback(() => {
        setError(null);
        setIsInitialized(false);

        try {
            syncActiveRef.current = false;
            clearHotspotIntervals();
            forceLoseWebGLContext();

            if (krpanoRef.current && typeof krpanoRef.current.call === 'function') {
                try {
                    krpanoRef.current.call('removeall');
                } catch (e) {
                    console.warn('⚠️ Error cleaning up krpano instance:', e);
                }
            }
            if (window.removepano) {
                window.removepano(krpanoPanoId);
            }
        } catch (cleanupError) {
            console.warn('⚠️ Warning during cleanup:', cleanupError);
        }

        krpanoRef.current = null;
        setIsReady(false);
        setMinimapData(null);
        currentMarkerDisplayRef.current = null;
        setLocations([]);
        setMarkerTypes([]);

        setTimeout(() => {
            if (!initializationRef.current) {
                initialize().catch((err) => {
                    console.error('❌ Retry initialization failed:', err);
                    const errorMessage = err instanceof Error ? err.message : String(err);
                    if (errorMessage.includes('timed out')) {
                        setError('timeout');
                    } else {
                        setError(`Retry failed: ${errorMessage}`);
                    }
                    setIsLoading(false);
                });
            }
        }, 500);
    }, [initialize, krpanoPanoId, clearHotspotIntervals, forceLoseWebGLContext]);

    /**
     * Zoom In
     */
    const zoomIn = useCallback(() => {
        const krpano = krpanoRef.current;
        if (krpano) {
            const isTileMode = krpano.get('layer[map].visible') !== true;
            if (isTileMode) {
                const currentFov = Number(krpano.get('view.fov')) || 90;
                krpano.call(`tween(view.fov, ${Math.max(1.0, currentFov / 1.5)}, 0.25, easeOutQuad);`);
            } else {
                const currentScale = Number(krpano.get('layer[map].scale')) || 0.5;
                const x = Number(krpano.get('layer[map].x')) || 0;
                const y = Number(krpano.get('layer[map].y')) || 0;
                const maxZoom = getMaxZoomLevel();
                const newScale = Math.min(currentScale * 1.5, maxZoom);
                krpano.set('layer[map].scale', newScale);
                krpano.set('layer[map].x', x * (newScale / currentScale));
                krpano.set('layer[map].y', y * (newScale / currentScale));
            }
        }
    }, [getMaxZoomLevel]);

    /**
     * Zoom Out
     */
    const zoomOut = useCallback(() => {
        const krpano = krpanoRef.current;
        if (krpano) {
            const isTileMode = krpano.get('layer[map].visible') !== true;
            if (isTileMode) {
                const currentFov = Number(krpano.get('view.fov')) || 90;
                const fovMax = Number(krpano.get('view.fovmax')) || 179;
                krpano.call(`tween(view.fov, ${Math.min(fovMax, currentFov * 1.5)}, 0.25, easeOutQuad);`);
            } else {
                const minScale = baseScaleRef.current > 0 ? baseScaleRef.current : MIN_ZOOM;
                const currentScale = Number(krpano.get('layer[map].scale')) || minScale;
                const x = Number(krpano.get('layer[map].x')) || 0;
                const y = Number(krpano.get('layer[map].y')) || 0;
                const newScale = Math.max(currentScale / 1.5, minScale);
                krpano.set('layer[map].scale', newScale);
                krpano.set('layer[map].x', x * (newScale / currentScale));
                krpano.set('layer[map].y', y * (newScale / currentScale));
            }
        }
    }, []);

    /**
     * Reset Zoom to initial (base) scale and center position
     */
    const resetZoom = useCallback(() => {
        const krpano = krpanoRef.current;
        if (krpano) {
            const isTileMode = krpano.get('layer[map].visible') !== true;
            if (isTileMode) {
                const fovMax = Number(krpano.get('view.fovmax')) || 120;
                krpano.call(`tween(view.fov, ${fovMax}, 0.25, easeOutQuad); tween(view.hlookat, 0, 0.25, easeOutQuad); tween(view.vlookat, 0, 0.25, easeOutQuad);`);
            } else {
                const targetScale = baseScaleRef.current > 0 ? baseScaleRef.current : MIN_ZOOM;
                krpano.set('layer[map].align', 'center');
                krpano.set('layer[map].edge', 'center');
                krpano.call('layer[map].changeorigin(center, center);');
                krpano.set('layer[map].scale', targetScale);
                krpano.set('layer[map].x', 0);
                krpano.set('layer[map].y', 0);
            }
        }
    }, []);

    // Map Interaction Handlers (Zoom & Pan)
    useEffect(() => {
        // Skip if zoom/pan is disabled
        if (!enableZoomPan) return;

        const element = document.getElementById(targetElementId);
        if (!element || !isReady || noMapData) return;

        const state = {
            isDragging: false,
            startX: 0,
            startY: 0,
            initialLayerX: 0,
            initialLayerY: 0,
            initialPinchDistance: 0,
            initialScale: 1,
            isPinching: false,
            rafId: 0 as number,
            pendingX: null as number | null,
            pendingY: null as number | null,
            pendingScale: null as number | null,
        };

        const scheduleLayerUpdate = (nextX: number, nextY: number, nextScale?: number) => {
            state.pendingX = nextX;
            state.pendingY = nextY;
            state.pendingScale = typeof nextScale === 'number' ? nextScale : null;

            if (state.rafId) {
                return;
            }

            state.rafId = window.requestAnimationFrame(() => {
                state.rafId = 0;
                const krpano = krpanoRef.current;
                if (!krpano || state.pendingX === null || state.pendingY === null) {
                    return;
                }

                if (typeof state.pendingScale === 'number') {
                    krpano.set('layer[map].scale', state.pendingScale);
                }
                krpano.set('layer[map].x', state.pendingX);
                krpano.set('layer[map].y', state.pendingY);
            });
        };

        const getLayerCoords = () => {
            const krpano = krpanoRef.current;
            if (!krpano || typeof krpano.get !== 'function') return { x: 0, y: 0, scale: 1 };
            return {
                x: Number(krpano.get('layer[map].x')) || 0,
                y: Number(krpano.get('layer[map].y')) || 0,
                scale: Number(krpano.get('layer[map].scale')) || 0.5,
            };
        };

        const isTileModeActive = () => {
            const krpano = krpanoRef.current;
            return krpano ? krpano.get('layer[map].visible') !== true : false;
        };

        const handleWheel = (e: WheelEvent) => {
            if (isTileModeActive()) return; // Native Krpano fov zoom handles this
            e.preventDefault(); // Prevent page scroll
            e.stopPropagation();
            const krpano = krpanoRef.current;
            if (!krpano) return;

            const { x, y, scale } = getLayerCoords();
            const normalizedDeltaY = e.deltaMode === WheelEvent.DOM_DELTA_LINE ? e.deltaY * 16 : e.deltaY;

            // Exponential wheel zoom keeps both directions responsive across
            // touchpads/mice and at different current scales.
            const zoomFactor = Math.exp(-normalizedDeltaY * 0.0015);
            let newScale = scale * zoomFactor;
            const minZoom = baseScaleRef.current > 0 ? baseScaleRef.current : MIN_ZOOM;
            newScale = Math.min(Math.max(newScale, minZoom), getMaxZoomLevel());

            // Calculate x and y to zoom around pointer
            const rect = element.getBoundingClientRect();
            const pX = e.clientX - rect.left;
            const pY = e.clientY - rect.top;
            const dX = pX - rect.width / 2;
            const dY = pY - rect.height / 2;

            const newX = dX - (dX - x) * (newScale / scale);
            const newY = dY - (dY - y) * (newScale / scale);

            scheduleLayerUpdate(newX, newY, newScale);
        };

        const handlePointerDown = (e: PointerEvent) => {
            if (isTileModeActive()) return;
            // Touch pointers are handled by touch events below.
            // Handling them here can steal tap events from marker onclick on mobile.
            if (e.pointerType === 'touch') return;
            // Only handle left click or touch
            if (e.button !== 0 && e.pointerType === 'mouse') return;

            state.isDragging = true;
            state.startX = e.clientX;
            state.startY = e.clientY;

            const { x, y } = getLayerCoords();
            state.initialLayerX = x;
            state.initialLayerY = y;

            // Do not capture a press here: that retargets pointerup away from
            // KRPano's marker and cancels its click. Capture only a real drag.
        };

        const handlePointerMove = (e: PointerEvent) => {
            if (e.pointerType === 'touch') return;
            if (e.buttons === 0) {
                state.isDragging = false;
                return;
            }
            if (!state.isDragging || state.isPinching) return;

            const dx = e.clientX - state.startX;
            const dy = e.clientY - state.startY;

            if (Math.hypot(dx, dy) < 4 && !element.hasPointerCapture?.(e.pointerId)) return;
            try {
                if (!element.hasPointerCapture(e.pointerId)) element.setPointerCapture(e.pointerId);
            } catch {
                // Synthetic pointer events may not support capture.
            }

            const krpano = krpanoRef.current;
            if (krpano) {
                scheduleLayerUpdate(state.initialLayerX + dx, state.initialLayerY + dy);
            }
        };

        const handlePointerUp = (e: PointerEvent) => {
            if (e.pointerType === 'touch') return;
            state.isDragging = false;
            try {
                if (element.hasPointerCapture(e.pointerId)) {
                    element.releasePointerCapture(e.pointerId);
                }
            } catch {
                // Ignore pointer capture release failures for forwarded pointer events.
            }
        };

        // Touch events for Pinch Zoom
        // Registered with { capture: true } so our handlers fire BEFORE KRPano's
        // internal canvas can consume the event. preventDefault() on 2-finger touches
        // fully blocks KRPano from interfering with pinch gestures.
        const handleTouchStart = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                // Block native behavior for multi-touch pinch gestures
                e.preventDefault();
                e.stopPropagation();
                state.isDragging = false;
                
                // Hardware touch drivers occasionally report secondary touches at the same coordinate initially.
                // We set a marker (-1) and delay capturing initial properties until the move event where it's stable.
                state.initialPinchDistance = -1;
                state.isPinching = false;
            } else if (e.touches.length === 1 && !isTileModeActive()) {
                // Single-finger pan: capture start position for drag.
                // Do NOT preventDefault here — that would block tap/click on markers.
                state.isDragging = true;
                state.startX = e.touches[0].clientX;
                state.startY = e.touches[0].clientY;
                const { x, y } = getLayerCoords();
                state.initialLayerX = x;
                state.initialLayerY = y;
            }
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (isTileModeActive()) return;
            if (e.touches.length === 2) {
                e.preventDefault();
                e.stopPropagation();
                const touch1 = e.touches[0];
                const touch2 = e.touches[1];
                const currentDistance = Math.hypot(
                    touch2.clientX - touch1.clientX,
                    touch2.clientY - touch1.clientY
                );

                // Initialize pinch safely once physical spread distance is stable
                if (state.initialPinchDistance === -1) {
                    if (currentDistance >= 20) {
                        state.initialPinchDistance = currentDistance;
                        const { x, y, scale } = getLayerCoords();
                        state.initialScale = scale;
                        state.initialLayerX = x;
                        state.initialLayerY = y;
                        
                        const rect = element.getBoundingClientRect();
                        state.startX = ((touch1.clientX + touch2.clientX) / 2) - rect.left;
                        state.startY = ((touch1.clientY + touch2.clientY) / 2) - rect.top;
                        
                        state.isPinching = true;
                    }
                    return; // Yield this frame
                }

                if (state.isPinching && state.initialPinchDistance >= 20) {
                    const scaleFactor = currentDistance / state.initialPinchDistance;
                    let newScale = state.initialScale * scaleFactor;
                    const minZoom = baseScaleRef.current > 0 ? baseScaleRef.current : MIN_ZOOM;
                    newScale = Math.min(Math.max(newScale, minZoom), getMaxZoomLevel());

                    const rect = element.getBoundingClientRect();
                    const currentPx = ((touch1.clientX + touch2.clientX) / 2) - rect.left;
                    const currentPy = ((touch1.clientY + touch2.clientY) / 2) - rect.top;

                    const dXInitial = state.startX - rect.width / 2;
                    const dYInitial = state.startY - rect.height / 2;
                    
                    const dXCurrent = currentPx - rect.width / 2;
                    const dYCurrent = currentPy - rect.height / 2;

                    const newX = dXInitial - (dXInitial - state.initialLayerX) * (newScale / state.initialScale);
                    const newY = dYInitial - (dYInitial - state.initialLayerY) * (newScale / state.initialScale);

                    const finalX = newX + (dXCurrent - dXInitial);
                    const finalY = newY + (dYCurrent - dYInitial);

                    const krpano = krpanoRef.current;
                    if (krpano) {
                        scheduleLayerUpdate(finalX, finalY, newScale);
                    }
                }
            } else if (e.touches.length === 1 && state.isDragging && !state.isPinching) {
                // Single-finger pan: move the map layer.
                // preventDefault stops pull-to-refresh and scroll-away on Android Chrome.
                e.preventDefault();
                const dx = e.touches[0].clientX - state.startX;
                const dy = e.touches[0].clientY - state.startY;
                scheduleLayerUpdate(state.initialLayerX + dx, state.initialLayerY + dy);
            }
        };

        const handleTouchEnd = (e: TouchEvent) => {
            if (e.touches.length < 2) {
                state.isPinching = false;
                // Re-sync drag start position so single-finger pan
                // picks up from the correct position after pinch ends
                if (e.touches.length === 1) {
                    state.isDragging = true;
                    state.startX = e.touches[0].clientX;
                    state.startY = e.touches[0].clientY;
                    const { x, y } = getLayerCoords();
                    state.initialLayerX = x;
                    state.initialLayerY = y;
                } else {
                    state.isDragging = false;
                }
            }
        };

        // iOS Safari proprietary gesture events: gesturestart, gesturechange, gestureend.
        // Safari fires these alongside touchstart/touchmove for pinch/rotate gestures.
        // If not prevented, Safari interprets them as page-level zoom/navigation,
        // which can cause the page to reload on iPhone.
        const handleGestureEvent = (e: Event) => {
            e.preventDefault();
            e.stopPropagation();
        };

        // touchforcechange fires on 3D Touch / Force Touch devices.
        // If not blocked, iOS can interpret force touches as "Peek & Pop" or
        // other system-level interactions that disrupt the map.
        const handleTouchForceChange = (e: Event) => {
            e.preventDefault();
        };

        // Add Listeners
        // Capture phase helps us receive wheel input before internal KRPano handlers.
        element.addEventListener('wheel', handleWheel, { capture: true, passive: false });
        element.addEventListener('pointerdown', handlePointerDown);
        element.addEventListener('pointermove', handlePointerMove);
        element.addEventListener('pointerup', handlePointerUp);
        element.addEventListener('pointercancel', handlePointerUp);

        // Use capture phase so we intercept before KRPano's canvas handles them
        element.addEventListener('touchstart', handleTouchStart, { capture: true, passive: false });
        element.addEventListener('touchmove', handleTouchMove, { capture: true, passive: false });
        element.addEventListener('touchend', handleTouchEnd, { capture: true });

        // iOS Safari gesture events — prevent native zoom/rotate interpretation
        element.addEventListener('gesturestart', handleGestureEvent, { capture: true, passive: false } as any);
        element.addEventListener('gesturechange', handleGestureEvent, { capture: true, passive: false } as any);
        element.addEventListener('gestureend', handleGestureEvent, { capture: true, passive: false } as any);

        // 3D Touch / Force Touch prevention
        element.addEventListener('touchforcechange', handleTouchForceChange, { passive: false } as any);

        // Document-level gesture prevention: When map is mounted, prevent
        // document-level gestures that could cause page navigation/reload.
        // This catches gestures that start on child elements (KRPano canvas)
        // and bubble up.
        const docGestureHandler = (e: Event) => {
            // Only prevent if the gesture originated within our map element
            if (element.contains(e.target as Node)) {
                e.preventDefault();
            }
        };
        document.addEventListener('gesturestart', docGestureHandler, { passive: false } as any);
        document.addEventListener('gesturechange', docGestureHandler, { passive: false } as any);
        document.addEventListener('gestureend', docGestureHandler, { passive: false } as any);

        return () => {
            if (state.rafId) {
                window.cancelAnimationFrame(state.rafId);
            }
            element.removeEventListener('wheel', handleWheel, { capture: true });
            element.removeEventListener('pointerdown', handlePointerDown);
            element.removeEventListener('pointermove', handlePointerMove);
            element.removeEventListener('pointerup', handlePointerUp);
            element.removeEventListener('pointercancel', handlePointerUp);

            element.removeEventListener('touchstart', handleTouchStart, { capture: true });
            element.removeEventListener('touchmove', handleTouchMove, { capture: true });
            element.removeEventListener('touchend', handleTouchEnd, { capture: true });

            element.removeEventListener('gesturestart', handleGestureEvent, { capture: true });
            element.removeEventListener('gesturechange', handleGestureEvent, { capture: true });
            element.removeEventListener('gestureend', handleGestureEvent, { capture: true });
            element.removeEventListener('touchforcechange', handleTouchForceChange);

            document.removeEventListener('gesturestart', docGestureHandler);
            document.removeEventListener('gesturechange', docGestureHandler);
            document.removeEventListener('gestureend', docGestureHandler);
        };
    }, [isReady, noMapData, targetElementId, enableZoomPan, getMaxZoomLevel]);

    // Sync mapTransform state with KRPano layer values
    useEffect(() => {
        if (!isReady || noMapData) return;

        let animationFrameId: number;
        let lastScale = 1;
        let lastX = 0;
        let lastY = 0;
        let lastImageWidth = 0;
        let lastImageHeight = 0;
        let lastReactSync = 0;
        // Throttle KRPano get() calls to ~30fps to reduce CPU load on mid-range Android.
        // DOM overlay updates still run every frame when values change.
        let lastPollTime = 0;
        const POLL_INTERVAL_MS = 32;

        const containerElement = document.getElementById(targetElementId);
        let containerWidth = containerElement ? containerElement.offsetWidth : 0;
        let containerHeight = containerElement ? containerElement.offsetHeight : 0;

        let resizeTimeout: NodeJS.Timeout;
        let resizeObserver: ResizeObserver | null = null;
        if (containerElement && window.ResizeObserver) {
            resizeObserver = new ResizeObserver(() => {
                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(() => {
                    if (containerElement) {
                        const r = containerElement.getBoundingClientRect();
                        containerWidth = r.width;
                        containerHeight = r.height;
                        // Force DOM re-calculation on next frame
                        lastScale = -1;
                    }
                }, 100);
            });
            resizeObserver.observe(containerElement);
        }

        syncActiveRef.current = true;

        const syncTransform = () => {
            if (!syncActiveRef.current) {
                return;
            }

            const now = performance.now();
            // Throttle KRPano polling to save CPU on weaker devices
            if (now - lastPollTime < POLL_INTERVAL_MS) {
                animationFrameId = requestAnimationFrame(syncTransform);
                return;
            }
            lastPollTime = now;

            const krpano = krpanoRef.current;
            if (krpano && typeof krpano.get === 'function') {
                if (krpano.get('layer[map].visible') !== true) {
                    // Do not sync layer[map] properties for DOM overlay if using Tile Map
                    animationFrameId = requestAnimationFrame(syncTransform);
                    return; 
                }

                const scale = Number(krpano.get('layer[map].scale')) || 1;
                const x = Number(krpano.get('layer[map].x')) || 0;
                const y = Number(krpano.get('layer[map].y')) || 0;
                const imageWidth = Number(krpano.get('layer[map].imagewidth')) || 0;
                const imageHeight = Number(krpano.get('layer[map].imageheight')) || 0;

                // Capture the initial (base) scale once the map image has loaded
                if (imageWidth > 0 && baseScaleRef.current === 0 && scale > 0) {
                    baseScaleRef.current = scale;
                }

                const overlayElement = document.getElementById(`${targetElementId}_overlay`);
                const needsInitialConfig = overlayElement && overlayElement.style.width === '';

                // Only touch DOM / React if values actually changed or overlay needs initial config
                if (needsInitialConfig || scale !== lastScale || x !== lastX || y !== lastY || imageWidth !== lastImageWidth || imageHeight !== lastImageHeight) {
                    
                    if (overlayElement && containerWidth > 0 && containerHeight > 0) {
                        const renderedW = imageWidth * scale;
                        const renderedH = imageHeight * scale;
                        const left = containerWidth / 2 + x - renderedW / 2;
                        const top = containerHeight / 2 + y - renderedH / 2;

                        // GPU-accelerated translate for panning
                        // Width/height updated dynamically so SVG's vectorEffect="non-scaling-stroke" maintains thick borders correctly
                        overlayElement.style.transformOrigin = '0 0';
                        overlayElement.style.transform = `translate(${left}px, ${top}px)`;
                        overlayElement.style.width = `${renderedW}px`;
                        overlayElement.style.height = `${renderedH}px`;
                        
                        // Keep left/top at 0 since we strictly use translate
                        if (overlayElement.style.left !== '0px') {
                            overlayElement.style.left = '0px';
                            overlayElement.style.top = '0px';
                        }
                    }

                    lastScale = scale;
                    lastX = x;
                    lastY = y;
                    
                    // Throttle React State updates to save CPU 
                    // Only sync imageWidth and imageHeight since React components only use dimensions
                    if (imageWidth !== lastImageWidth || imageHeight !== lastImageHeight) {
                        lastImageWidth = imageWidth;
                        lastImageHeight = imageHeight;

                        const now = Date.now();
                        if (now - lastReactSync > 40) {
                            setMapTransform(prev => ({ ...prev, imageWidth, imageHeight }));
                            lastReactSync = now;
                        }
                    }
                }
            }
            animationFrameId = requestAnimationFrame(syncTransform);
        };

        animationFrameId = requestAnimationFrame(syncTransform);

        return () => {
            syncActiveRef.current = false;
            cancelAnimationFrame(animationFrameId);
            clearTimeout(resizeTimeout);
            if (resizeObserver && containerElement) {
                resizeObserver.disconnect();
            }
        };
    }, [isReady, noMapData, targetElementId]);

    // Expose global functions for KRPano
    useEffect(() => {
        // Initialize handlers map if needed
        if (!window.changeMapHandlers) {
            window.changeMapHandlers = {};
        }
        // Set instance-specific handler
        window.changeMapHandlers[targetElementId] = changeMap;
        window.changeViewByMarkerHandlers ??= {};
        window.changeViewByMarkerHandlers[targetElementId] = changeViewByMarker;
        // Keep global for backwards compatibility
        window.changeMap = changeMap;
        window.changeViewByMarker = changeViewByMarker;

        return () => {
            try {
                // Clean up instance-specific handler
                if (window.changeMapHandlers?.[targetElementId] === changeMap) {
                    delete window.changeMapHandlers[targetElementId];
                }
                if (window.changeViewByMarkerHandlers?.[targetElementId] === changeViewByMarker) {
                    delete window.changeViewByMarkerHandlers[targetElementId];
                }
                if (window.changeMap === changeMap) delete (window as any).changeMap;
                if (window.changeViewByMarker === changeViewByMarker) delete (window as any).changeViewByMarker;
            } catch (err) {
                console.warn('Warning cleaning up global functions:', err);
            }
        };
    }, [changeMap, changeViewByMarker, targetElementId]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            try {
                syncActiveRef.current = false;
                clearHotspotIntervals();
                forceLoseWebGLContext();

                if (typeof window.removepano === 'function') {
                    window.removepano(krpanoPanoId);
                }
            } catch (err) {
                console.error('❌ Error removing Krpano instance:', err);
            }
            krpanoRef.current = null;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Register / cleanup window tooltip & selection functions for KRPano
    useEffect(() => {
        const safeId = (targetElementId || DEFAULT_TARGET_ID).replace(/[^a-zA-Z0-9_]/g, '_');
        const showKey = `__mmShow_${safeId}`;
        const hideKey = `__mmHide_${safeId}`;
        const selectKey = `__mmSelect_${safeId}`;
        const clickKey = `__mmClick_${safeId}`;
        (window as any)[clickKey] = (locId: number | string) => markerClickHandlersRef.current[String(locId)]?.();
        (window as any)[showKey] = (layerName: string, locId?: number | string) => {
            const text = markerTitlesRef.current[layerName];
            if (text) setHoverTooltip({ text, visible: true });
            const resolvedId = locId !== undefined && locId !== null ? locId : layerName.replace(/^marker_/, '');
            onMarkerHoverRef.current?.(resolvedId);
        };
        (window as any)[hideKey] = () => {
            setHoverTooltip(prev => ({ ...prev, visible: false }));
            onMarkerHoverRef.current?.(null);
        };
        (window as any)[selectKey] = (locId: number | string) => {
            onMarkerSelectRef.current?.(locId);
        };
        return () => {
            delete (window as any)[showKey];
            delete (window as any)[hideKey];
            delete (window as any)[selectKey];
            delete (window as any)[clickKey];
        };
    }, [targetElementId]);

    // Reactively elevate & scale marker when hoveredLocationId changes from external source (e.g. list hover)
    const hoveredLocationId = options.hoveredLocationId;
    useEffect(() => {
        const krpano = krpanoRef.current;
        if (!krpano || typeof krpano.set !== 'function' || typeof krpano.get !== 'function') return;

        const isMapVis = krpano.get('layer[map].visible');
        const defaultElementType = (isMapVis !== true && isMapVis !== 'true' && isMapVis !== 1 && isMapVis !== '1') ? 'hotspot' : 'layer';

        const getElementType = (targetName: string): 'layer' | 'hotspot' => {
            if (krpano.get(`layer[${targetName}].name`)) return 'layer';
            if (krpano.get(`hotspot[${targetName}].name`)) return 'hotspot';
            return defaultElementType;
        };

        const prevLayerName = currentlyHoveredMarkerRef.current;
        const targetLayerName = hoveredLocationId !== null && hoveredLocationId !== undefined && hoveredLocationId !== ''
            ? `marker_${hoveredLocationId}`
            : null;

        if (prevLayerName === targetLayerName) return;

        // Restore previous hovered marker scale & zorder
        if (prevLayerName) {
            const elementType = getElementType(prevLayerName);
            const baseScale = markerBaseScalesRef.current[prevLayerName] ?? 1.0;
            const baseZorder = markerBaseZordersRef.current[prevLayerName] ?? 10;
            try {
                krpano.set(`${elementType}[${prevLayerName}].scale`, baseScale);
                krpano.set(`${elementType}[${prevLayerName}].zorder`, baseZorder);
            } catch {}
            currentlyHoveredMarkerRef.current = null;
        }

        // Elevate and scale newly hovered marker
        if (targetLayerName) {
            const elementType = getElementType(targetLayerName);
            try {
                const exists = krpano.get(`${elementType}[${targetLayerName}].visible`);
                if (exists !== null && exists !== undefined) {
                    const baseScale = markerBaseScalesRef.current[targetLayerName] ?? (Number(krpano.get(`${elementType}[${targetLayerName}].scale`)) || 1.0);
                    krpano.set(`${elementType}[${targetLayerName}].zorder`, 9999);
                    krpano.set(`${elementType}[${targetLayerName}].scale`, baseScale);
                    currentlyHoveredMarkerRef.current = targetLayerName;
                }
            } catch {}
        }
    }, [hoveredLocationId]);

    // Reactively update marker presentation (active scene palette, selection ring)
    const selectedLocationId = options.selectedLocationId;
    const currentScene = options.currentScene;
    useEffect(() => {
        const krpano = krpanoRef.current;
        if (!krpano || typeof krpano.set !== 'function' || typeof krpano.get !== 'function') return;
        if (!locations || locations.length === 0) return;

        let isMounted = true;
        const targetScene = currentScene;
        const targetMapId = minimapDataRef.current?.id ?? options.mapId;
        const targetKrpano = krpano;

        const updateMarkers = async () => {
            const currentLoadedMapId = minimapDataRef.current?.id;
            if (targetMapId && currentLoadedMapId && String(targetMapId) !== String(currentLoadedMapId)) {
                return;
            }

            const isMapVis = krpano.get('layer[map].visible');
            const isTileMode = isMapVis !== true && isMapVis !== 'true' && isMapVis !== 1 && isMapVis !== '1';
            const mapMarkerDisplayToUse = currentMarkerDisplayRef.current ?? options.mapMarkerDisplay;

            // 1. Cập nhật ngay trạng thái active theo scene khớp trực tiếp trước (Zero-delay)
            for (const item of locations) {
                try {
                    const currentMapId = minimapDataRef.current?.id;
                    if (item.minimap_id && currentMapId && Number(item.minimap_id) !== Number(currentMapId)) {
                        continue;
                    }

                    const layerName = `marker_${item.id}`;
                    let elementType: 'hotspot' | 'layer' = isTileMode ? 'hotspot' : 'layer';
                    const exists = krpano.get(`${elementType}[${layerName}].visible`);
                    if (exists === null || exists === undefined) {
                        const fallbackType = elementType === 'layer' ? 'hotspot' : 'layer';
                        const fallbackExists = krpano.get(`${fallbackType}[${layerName}].visible`);
                        if (fallbackExists !== null && fallbackExists !== undefined) {
                            elementType = fallbackType;
                        } else {
                            await createMarker(item, mapMarkerDisplayToUse);
                            continue;
                        }
                    }

                    const isCurrentSceneDirect = Boolean(targetScene && isLocationMatchingScene(item, targetScene));
                    const isExplicitlySelected = selectedLocationId !== null && selectedLocationId !== undefined && String(selectedLocationId) === String(item.id);
                    const hasExplicitSelection = selectedLocationId !== null && selectedLocationId !== undefined && selectedLocationId !== '';
                    const isActiveDirect = hasExplicitSelection ? isExplicitlySelected : isCurrentSceneDirect;

                    applyMarkerPresentation(krpano, item, isActiveDirect, isTileMode, mapMarkerDisplayToUse);
                } catch (markerErr) {
                    console.warn(`Error updating marker_${item.id}:`, markerErr);
                }
            }

            // 2. Tra nhóm scene ở nền (Background Group Query)
            if (!isMapForAdmin && targetScene) {
                const candidateGroupLinkedMarkers = locations.filter((marker: EnhancedProcessedLocation) => {
                    if (marker.type !== 'scene' && marker.type !== 'view') return false;
                    const markerConfig = parseMarkerConfigObject(marker.config);
                    return markerConfig.scene_link_type === 'group_scene';
                });

                resolveGroupsInBackground(targetScene, targetMapId, targetKrpano, candidateGroupLinkedMarkers);
            }
        };

        updateMarkers();

        return () => {
            isMounted = false;
        };
    }, [currentScene, selectedLocationId, locations, isMapForAdmin, getSceneGroupIdsBySceneName, toProxyImageUrl, locale, resolveMarkerTitleForLocale, options.mapMarkerDisplay, createMarker, applyMarkerPresentation, resolveGroupsInBackground]);

    return {
        // State
        isLoading,
        isReady,
        error,
        noMapData,
        minimapData,
        locations,
        markerTypes,
        mapTransform,

        // Refs
        krpanoRef,
        targetElementRef,

        // Actions
        initialize,
        changeMap,
        changeViewByMarker,
        goBack,
        retry,
        zoomIn,
        zoomOut,
        resetZoom,
        preloadMapImage,

        // History
        mapHistory,
        canGoBack: mapHistory.size() > 1,

        // Hover Tooltip
        hoverTooltip,
        hideTooltip: () => setHoverTooltip(prev => ({ ...prev, visible: false })),
    };
}

export default useKrpanoMap;
